import styled, { css } from 'styled-components'
import {
    svgStroke,
    fromTablet, belowTablet, fromPhone, belowPhone
} from 'utilities/styled'
import { containerBoxCss } from 'utilities/widget'
import { Info } from 'styled-icons/feather'

export const Wrapper = styled.span`
    anchor-scope: --test-anchor;
`

const fixedContent = css`
    position: fixed;
    bottom: 0;
    left: 0;
    position-area: unset !important;
    position-try-fallbacks: unset !important;
    overscroll-behavior: contain;

    border-width: 0;
    border-radius: 16px 16px 0 0;
    width: 100%;
    max-width: 100%;
    max-height: 80svh;
    background-color: var(--huma-color-surface-elevated);
    box-shadow: 0 -8px 16px 0 var(--huma-color-shadow);

    --huma-color-surface-default: var(--huma-color-surface-elevated);
`

export const PopoverContent = styled.span`
    position: absolute;
    inset: auto;
    display: none;

    margin: 0;
    max-width: min(calc(100vw - 16px), 350px);

    &:popover-open {
        display: block;
    }

    &:empty {
        display: none;
    }

    :where(dialog, [role="dialog"], [role="complementary"]) & {
        position: fixed; /* Since a the content behind a modal can be scrolled, this is needed to keep the popover in place */
    }

    &.tooltip {
        border: 1px solid var(--huma-color-border-default);
        border-radius: 4px;
        width: max-content;
        padding: 8px;
        background-color: var(--huma-color-surface-elevated);
        box-shadow: var(--huma-shadow-low);

        opacity: 0;
        scale: .7;

        white-space: normal;
        overflow-wrap: anywhere;
        font-size: 14px;
        font-weight: normal;
        line-height: 24px;
        color: var(--huma-color-foreground-default);

        &:popover-open {
            transition-property: opacity, scale;
            transition-duration: var(--__huma-component-popover-duration);
            opacity: 1;
            scale: 1;

            @starting-style {
                opacity: 0;
                scale: .7;
            }
        }

        &[class*="top"] {
            transform-origin: bottom center;
        }

        &[class*="bottom"] {
            transform-origin: top center;
        }
    }

    &.widget {
        color: var(--huma-color-foreground-default);

        &.boxy {
            ${containerBoxCss}
        }

        ${fromTablet`
            ${containerBoxCss}
        `}

        ${belowTablet`
            ${fixedContent}

            display: flex;
            flex-direction: column;

            padding-inline: 24px;
        `}
    }

    &.column-configurator {
        width: 352px;
        padding: 32px;

        ${fromPhone`
            ${containerBoxCss}
        `}

        ${belowPhone`
            ${fixedContent}

            display: flex;
            flex-direction: column;

            padding-inline: 24px;
        `}
    }

    &.context-menu {
        ${belowTablet`
            ${fixedContent}
        `}
    }

    /* Vertical */
    &[class*="top"],
    &[class*="bottom"] {
        position-try-fallbacks:
            flip-block,
            flip-inline,
            flip-block flip-inline;
    }

    /* Top */
    &[class*="top"] {
        margin-block-start: var(--huma-global-main-layout-header-height);
        margin-block-end: var(--__huma-component-popover-distance-y);
    }
    &.top { position-area: top; }
    &.top-center { position-area: top center; }
    &.top-start { position-area: top span-right; }
    &.top-end { position-area: top span-left; }

    /* Bottom */
    &[class*="bottom"] {
        margin-block-start: var(--__huma-component-popover-distance-y);
    }
    &.bottom { position-area: bottom; }
    &.bottom-center { position-area: bottom center; }
    &.bottom-start { position-area: bottom span-right; }
    &.bottom-end { position-area: bottom span-left; }

    /* Horizontal */
    &[class*="left"],
    &[class*="right"] {
        position-try-fallbacks:
            flip-inline,
            flip-block,
            flip-inline flip-block;
    }

    /* Left */
    &[class*="left"] {
        margin-inline-start: var(--__huma-component-popover-distance-x);
    }
    &.left { position-area: left; }
    &.left-center { position-area: left center; }
    &.left-start { position-area: left span-bottom; }
    &.left-end { position-area: left span-top; }

    /* Right */
    &[class*="right"] {
        margin-inline-start: var(--__huma-component-popover-distance-x);
    }
    &.right { position-area: right; }
    &.right-center { position-area: right center; }
    &.right-start { position-area: right span-bottom; }
    &.right-end { position-area: right span-top; }
`

export const PopoverTrigger = styled.span``

export const TooltipTriggerWrapper = styled.span`
    cursor: help;
    display: inline-block;

    &:where(:not(.naked)) {
        vertical-align: top;

        padding-inline: 4px;

        line-height: ${p => p.$lineHeight ?? 20}px;
    }

    ${p => !!p.onClick && `
        cursor: pointer;
    `}

    :where(button, [role="button"]) > & {
        cursor: pointer;
    }

    &.relative {
        position: relative;
    }

    &.info,
    &.action {
        color: var(--huma-color-foreground-info-bold);
    }

    &.warning {
        color: var(--huma-color-foreground-warning);
    }

    &.error {
        color: var(--huma-color-foreground-error);
    }
`

export const TooltipTriggerIcon = styled(Info)`
    line-height: 16px;
    transition: color .15s ease-in-out;

    &:hover {
        color: var(--huma-color-foreground-constructive-bold);
    }

    ${svgStroke('small')}
`